import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Summary, Summary_Types, TabDetails } from '../../interfaces/summary.interface';
import { CollaborationService } from '../../services/collaboration.service';
import { BasicReusableComponentComponent } from '../basic-reusable-component/basic-reusable-component.component';
import { catchError, forkJoin, of } from 'rxjs';
import { FileTypes } from '../../interfaces/referral.interface';
import { DomSanitizer } from '@angular/platform-browser';
import { EmailWeavyData } from '../../interfaces/space.interface';
import { UnAuthorizedComponent } from '../../components/un-authorized/un-authorized.component';
import { NotificationSettingsComponent } from '../../components/notification-settings/notification-settings.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { NgClass, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTabGroup, MatTab } from '@angular/material/tabs';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { UserThumbNailComponent } from '../../components/user-thumb-nail/user-thumb-nail.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { AssetUrlPipe } from '../../pipes/asset-url.pipe';

@Component({
    selector: 'app-summary',
    templateUrl: './summary.component.html',
    styleUrl: './summary.component.scss',
    imports: [UnAuthorizedComponent, NotificationSettingsComponent, MatProgressSpinner, MatTooltip, NgClass, FormsModule, MatTabGroup, MatTab, InfiniteScrollDirective, UserThumbNailComponent, SpinnerComponent, DatePipe, AssetUrlPipe]
})
export class SummaryComponent extends BasicReusableComponentComponent implements OnInit {
  @Input() declare weavyData: EmailWeavyData;
  @Output() itemClicked = new EventEmitter<any>();
  searchText: any;
  tabLabels: string[] = ['@ mentions', 'everything', 'posts', 'files', 'comments'];
  summaryData: TabDetails[] = [];
  selectedTabIndex: number = 1;
  filteredSummaryData: any;
  labelTypeMatcher: any = {
    '@ mentions': Summary_Types.MENTION,
    'posts': Summary_Types.POST,
    'files': Summary_Types.FILE,
    'comments': Summary_Types.COMMENT
  };
  showOnDemandLoader = false;
  iconUrlMapper: any = {
    post: 'forum',
    comment: 'comment_lime',
    docx: "word_file",
    pptx: "ppt_file",
    xlsx: "excel_file",
    xlsb: "excel_file",
    xlsm: "excel_file",
    note: "file_document",
    pdf: "pdf",
    zip: "zip",
    eml: "email",
    png: "image_png",
    jpg: "image_png",
    svg: "image_png",
    txt: "text_grey",
    folder: "folder",
  };
  isUserUnAuthorized = false;
  errorStatusCode;
  isSummaryDataLoaded = false;
  maxRecordsInOneAPIRes = 100;
  constructor(override collaborationService: CollaborationService,
    public sanitizer: DomSanitizer) {
    super(collaborationService);
  }
  override ngOnInit() {
    super.ngOnInit();
  }

  override initialize(): void {
    this.showLoader = true;
    const summaryTypes: string[] = ['mentions', 'everything', 'posts', 'files', 'comments'];
    const summaryCalls = summaryTypes.map((type: string) => {
      return this.collaborationService.getSummaryDataOnDemand(type, this.weavyData.userEmail, '', this.maxRecordsInOneAPIRes, 0);
    })
    forkJoin(summaryCalls).subscribe({
      next: (response: any) => {
        this.isSummaryDataLoaded = true;
        this.showLoader = false;
        this.summaryData = [
          {
            name: "@ mentions",
            tabName: 'mentions',
            data: this.updateRTEDataForDisplay(response[0]),
            method: this.updateRTEDataForDisplay,
            pageCountDivider: this.maxRecordsInOneAPIRes * 2,
            searchTextData: '',
            oldSearchText: '',
            isAllDataLoaded: response[0].length < this.maxRecordsInOneAPIRes ? true : false
          },
          {
            name: "everything",
            tabName: "everything",
            data: this.updateEverythingDataForDisplay(response[1]),
            method: this.updateEverythingDataForDisplay,
            searchTextData: '',
            oldSearchText: '',
            pageCountDivider: this.maxRecordsInOneAPIRes * 4,
            isAllDataLoaded: response[1].length < this.maxRecordsInOneAPIRes ? true : false
          },
          {
            name: "posts",
            tabName: "posts",
            data: this.updateRTEDataForDisplay(response[2]),
            method: this.updateRTEDataForDisplay,
            searchTextData: '',
            oldSearchText: '',
            pageCountDivider: this.maxRecordsInOneAPIRes,
            isAllDataLoaded: response[2].length < this.maxRecordsInOneAPIRes ? true : false
          },
          {
            name: "files",
            tabName: "files",
            data: this.updateFileDataForDisplay(response[3]),
            method: this.updateFileDataForDisplay,
            searchTextData: '',
            oldSearchText: '',
            pageCountDivider: this.maxRecordsInOneAPIRes,
            isAllDataLoaded: response[3].length < this.maxRecordsInOneAPIRes ? true : false
          },
          {
            name: "comments",
            tabName: "comments",
            data: this.updateRTEDataForDisplay(response[4]),
            method: this.updateRTEDataForDisplay,
            searchTextData: '',
            oldSearchText: '',
            pageCountDivider: this.maxRecordsInOneAPIRes,
            isAllDataLoaded: response[4].length < this.maxRecordsInOneAPIRes ? true : false
          }
        ]
        this.tabChanged();
      },
      error: (err: any) => {
        this.isSummaryDataLoaded = true;
        this.showLoader = false;
        this.isUserUnAuthorized = true;
        this.errorStatusCode = err.status;
        this.summaryData = [];
        this.collaborationService.displayErrorMessage(err, 'Error while retrieving summery data. Please try after some time.');
      }
    });
  }

  updateRTEDataForDisplay(records: Summary[]) {
    return records.map((record: Summary) => {
      record.shortDescription = this.extractShortDescription(record.content);
      record.displayContent = this.sanitizer.bypassSecurityTrustHtml(this.extractTextFromHTML(record.content));
      record.imgUrl = this.iconUrlMapper[record.contentType === Summary_Types.MENTION ? record.mentionType?.toLowerCase() : record.contentType.toLowerCase()] ?? 'file';
      return record;
    });
  }

  updateFileDataForDisplay(records: Summary[]) {
    return records.map((record: Summary) => {
      if ([FileTypes.FOLDER, FileTypes.NOTE].includes(record.kind)) {
        record.imgUrl = this.iconUrlMapper[record.kind];
        let properties = JSON.parse(record.fileProperties);
        record.displayContent = properties?.body ? this.sanitizer.bypassSecurityTrustHtml(this.extractTextFromHTML(properties.Body)) : '';
      }
      else {
        let lastDotIndex = record.fileName.lastIndexOf('.');
        let extension = lastDotIndex === -1 ? record.kind : record.fileName.substring(lastDotIndex + 1);
        record.imgUrl = this.iconUrlMapper[extension] ?? 'file';
      }
      return record;
    });
  }

  updateEverythingDataForDisplay(records: Summary[]) {
    return records.map((record: Summary) => {
      record.shortDescription = record.contentType === Summary_Types.FILE ? '' : this.extractShortDescription(record.content);
      record.displayContent = record.contentType === Summary_Types.FILE ? '' : this.sanitizer.bypassSecurityTrustHtml(this.extractTextFromHTML(record.content));
      if (record.contentType === Summary_Types.FILE) {
        if ([FileTypes.FOLDER, FileTypes.NOTE].includes(record.kind)) {
          record.imgUrl = this.iconUrlMapper[record.kind];
          let properties = JSON.parse(record.fileProperties);
          record.displayContent = properties?.body ? this.sanitizer.bypassSecurityTrustHtml(this.extractTextFromHTML(properties.Body)) : '';
        }
        else {
          let lastDotIndex = record.fileName.lastIndexOf('.');
          let extension = lastDotIndex === -1 ? record.kind : record.fileName.substring(lastDotIndex + 1);
          record.imgUrl = this.iconUrlMapper[extension] ?? 'file';
        }
      }
      else {
        record.imgUrl = this.iconUrlMapper[record.contentType === Summary_Types.MENTION ? record.mentionType?.toLowerCase() : record.contentType.toLowerCase()] ?? 'file';
      }
      return record;
    });
  }

  extractShortDescription(htmlString: string) {
    const textString = this.extractTextFromHTML(htmlString, 1);
    const atIndex = textString.indexOf('@');
    if (atIndex > 0) {
      const atText = textString.substring(0, atIndex);
      return atText.length <= 100 ? this.sanitizer.bypassSecurityTrustHtml(atText) : null;
    }
    return null;
  }

  extractTextFromHTML(htmlString: string, percent = 0.2): any {
    const sanitizedHTML = htmlString.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    const text = sanitizedHTML.replace(/<[^>]*>/g, ' ');
    const trimmedText = text.replace(/\s+/g, ' ').trim();
    if (trimmedText.length <= 100) return trimmedText;
    else if (trimmedText.length <= 200) percent = 0.8;
    else if (trimmedText.length <= 400) percent = 0.5;
    const length = Math.floor(trimmedText.length * percent);
    const truncatedText = trimmedText.slice(0, length);
    return truncatedText + '...';
  }

  searchSubmit(selectedTabIndex: number) {
    const newSearchTextFromInput = this.searchText.trim();
    if (this.summaryData[selectedTabIndex].searchTextData === newSearchTextFromInput) {
      return;
    }
    this.summaryData[selectedTabIndex].oldSearchText = this.summaryData[selectedTabIndex].searchTextData;
    this.summaryData[selectedTabIndex].searchTextData = newSearchTextFromInput;
    this.summaryData[selectedTabIndex].isAllDataLoaded = false;
    this.filteredSummaryData = [];
    this.isSummaryDataLoaded = false;
    this.getSpecificTabDataOnDemandScroll(selectedTabIndex, true, false, true);
  }

  tabChanged() {
    this.filteredSummaryData = this.summaryData[this.selectedTabIndex].data;
  }

  handleSummaryItemClick(record: any) {
    record.id = record?.postId ? record.postId : record.id;
    this.itemClicked.emit(record);
  }

  notificationSettings() {
    this.collaborationService.setShowOrHideSideNav('summary', true);
  }

  getSpecificTabDataOnDemandScroll(selectedTabIndex: number, resetData = false, onDemandSCroll = true, fromSearchTetx = false) {
    let tabData = this.summaryData[selectedTabIndex];
    if (tabData.isAllDataLoaded) {
      return;
    }
    if (!onDemandSCroll) {
      this.showLoader = true;
    } else if (onDemandSCroll) {
      this.showOnDemandLoader = true;
    }
    const currentRecords = tabData.data.length && !resetData ? Math.ceil(tabData.data.length / tabData.pageCountDivider) : 0;
    this.collaborationService.getSummaryDataOnDemand(tabData.tabName, this.weavyData.userEmail, tabData.searchTextData, this.maxRecordsInOneAPIRes, currentRecords).subscribe({
      next: (response: any) => {
        this.showLoader = false;
        this.showOnDemandLoader = false;
        this.isSummaryDataLoaded = true;
        const methodToCall = tabData.method.bind(this);
        tabData = {
          ...tabData,
          data: resetData ? methodToCall(response) : [...tabData.data, ...methodToCall(response)],
          isAllDataLoaded: response.length < this.maxRecordsInOneAPIRes ? true : false
        }
        this.summaryData[selectedTabIndex] = tabData;
        this.filteredSummaryData = tabData.data;
      },
      error: (err: any) => {
        this.showOnDemandLoader = false;
        this.isSummaryDataLoaded = true;
        if (fromSearchTetx) {
          this.summaryData[selectedTabIndex].searchTextData = this.summaryData[selectedTabIndex].oldSearchText;
        }
        this.showLoader = false;
        this.collaborationService.displayErrorMessage(err, 'Error while retrieving summery data. Please try after some time.');
      }
    });
  }
}
